<!--
  Provides the template for the expandable application section bar
-->
<template>
  <div class="ApplicationSectionBar">
    <h2 v-if="!isPrintPreview" class="unstyled">
      <button
        type="button"
        @click="toggleSection"
        @focus="onFocus()"
        :aria-expanded="isSectionExpanded.toString()"
        :aria-controls="ariaControls"
        title="Click to expand or collapse this section"
        ref="sectionHeader"
        class="sectionHeader unstyled"
      >
        <v-avatar
          size="22"
          v-if="showHourGlassIcon"
          color="primary"
          class="mr-5"
          role="img"
          aria-hidden="false"
          title="Waiting on parent/carer"
        >
          <v-icon size="16" color="white">mdi-timer-sand-empty</v-icon>
        </v-avatar>
        <v-icon
          v-else-if="showErrorIcon"
          class="error--text pr-5"
          role="img"
          aria-hidden="false"
          :title="
            isSupportingDocsSection
              ? 'New documents to review'
              : 'Action required'
          "
        >
          error
        </v-icon>
        <div
          :class="
            showErrorIcon || showHourGlassIcon
              ? 'sectionTitle'
              : 'sectionTitle pl-11'
          "
        >
          {{ section.label }}
        </div>
        <font-awesome-icon
          focusable="false"
          class="icon"
          v-if="isSectionExpanded"
          icon="angle-up"
        />
        <font-awesome-icon
          focusable="false"
          class="icon"
          v-if="!isSectionExpanded"
          icon="angle-down"
        />
      </button>
    </h2>

    <div v-if="isPrintPreview" class="printPreviewSectionTitle">
      <h2>{{ sectionIndex }}. {{ section.label }}</h2>
    </div>
  </div>
</template>

<script>
import { STREAM, EA_TAB_FILTERING } from '@/constants'
import fieldHelperMixin from '@/helpers/fieldHelperMixin'
import { mapGetters } from 'vuex'

export default {
  name: 'ApplicationSectionBar',
  props: {
    section: {
      type: Object
    },
    sectionIndex: {
      type: [Number, String]
    }
  },
  mixins: [fieldHelperMixin],
  computed: {
    ...mapGetters({
      application: 'application',
      currentStream: 'currentStream'
    }),
    isArchived() {
      return this.currentStream === STREAM.ARCHIVE
    },
    ariaControls() {
      return this.isSectionExpanded ? this.section.id + '-fields' : false
    },
    isSectionExpanded() {
      return this.$store.state.sectionExpandedId === this.section.id
    },
    isSupportingDocsSection() {
      return this.section.id === 'supporting-documents'
    },
    notAllAdditionalDocsViewed() {
      return this.application?.supportingDocuments?.additionalDocuments?.some(
        (file) => file.isDocViewed === false
      )
    },
    showHourGlassIcon() {
      const APPLICATION_IS_ARCHIVED = this.isArchived
      const APPLICATION_IS_NOT_PENDING_RECORD = !EA_TAB_FILTERING[
        'PENDING'
      ].includes(this.application?.applicationStatus)
      const SECTION_IS_NOT_SUPPORTING_DOCS = !this.isSupportingDocsSection
      const ADDITIONAL_DOCS_IS_NOT_REQUIRED =
        this.application?.requireAdditionalDocs !== true
      const All_ADDITIONAL_DOCS_IS_NOT_VIEWED = this.notAllAdditionalDocsViewed
      if (APPLICATION_IS_ARCHIVED) {
        return false
      } else if (APPLICATION_IS_NOT_PENDING_RECORD) {
        return false
      } else if (SECTION_IS_NOT_SUPPORTING_DOCS) {
        return false
      } else if (ADDITIONAL_DOCS_IS_NOT_REQUIRED) {
        return false
      } else if (All_ADDITIONAL_DOCS_IS_NOT_VIEWED) {
        return false
      }
      return true
    },
    showErrorIcon() {
      const APPLICATION_IS_PENDING_RECORD = EA_TAB_FILTERING[
        'PENDING'
      ].includes(this.application?.applicationStatus)
      return (
        this.showMissingInfoIcon ||
        this.showConflictFieldsIcon ||
        this.showUnresolvedMatchingIcon ||
        this.showInvalidFieldsIcon ||
        (this.isSupportingDocsSection &&
          this.notAllAdditionalDocsViewed &&
          APPLICATION_IS_PENDING_RECORD &&
          !this.isArchived)
      )
    },
    showMissingInfoIcon() {
      return this.missingFields.find(
        (missingFieldSection) =>
          this.section.id === missingFieldSection.section.id
      )
    },
    showConflictFieldsIcon() {
      return this.conflictFields.find(
        (conflictFieldSection) =>
          this.section.id === conflictFieldSection.section.id
      )
    },
    showUnresolvedMatchingIcon() {
      return this.unresolvedMatching.find(
        (unresolvedMatchingSection) =>
          this.section.id === unresolvedMatchingSection.section.id
      )
    },
    showInvalidFieldsIcon() {
      return this.invalidFields.find(
        (invalidField) => this.section.id === invalidField.section.id
      )
    }
  },
  methods: {
    toggleSection() {
      if (
        this.$store.state.sectionExpandedId &&
        this.$store.state.sectionExpandedId !== this.section.id
      ) {
        // If switching between different open sections, scroll the section
        // header to the top of the viewport to avoid ending up with a "random"
        // scroll position (caused by hiding the original section)
        window.OES.scrollSectionToTop = true
      }
      this.$store.dispatch('toggleSection', this.section)
    },
    onFocus() {
      // If focused section bar is concealed below Save/Cancel bar, scroll it into view.
      // Timeout is required to prevent scroll interfering with the click event
      var distanceFromBottom =
        window.innerHeight - this.$el.getBoundingClientRect().bottom
      var saveBarHeight = 65
      if (this.$store.state.isEditing && distanceFromBottom < saveBarHeight) {
        window.scrollTo(
          window.pageXOffset,
          window.pageYOffset + (saveBarHeight - distanceFromBottom)
        )
      }
    }
  },
  updated() {
    // When opening a section, scroll the section bar to the top of the screen
    if (window?.OES?.scrollSectionToTop && this.isSectionExpanded) {
      var spacingForFocusOutline = 4
      window.OES.scrollSectionToTop = false
      window.scrollTo(
        window.pageXOffset,
        window.pageYOffset +
          this.$el.getBoundingClientRect().top -
          spacingForFocusOutline
      )
    }
  }
}
</script>

<style scoped lang="scss">
.ApplicationSectionBar {
  display: flex;

  h2 {
    width: 100%;
    margin-bottom: 0;

    .sectionHeader {
      height: 3.5rem;
      width: 100%;
      padding: 1rem 1rem 1rem 2rem;
      display: flex;
      align-items: center;
      border-top: 1px solid rgba(0, 0, 0, 0.12);
      &:focus {
        outline: none;
        background: $color-row-hover;
      }

      .icon {
        vertical-align: middle;
        height: 1.5em;
        width: 1.5em;
        padding: 0.25em;
        font-size: 0.8em;
      }

      .sectionTitle {
        flex: 1;
        h2 {
          margin: 0;
        }
      }
    }
  }

  .printPreviewSectionTitle {
    padding: 1rem 1rem 0;
    width: 100%;
    h2 {
      margin: 0;
      font-size: 1.4em;
      border-bottom: 1px solid;
      padding-bottom: 0.5rem;
    }
  }

  button:hover {
    background-color: $color-field-hover;
  }
}
</style>
